/**
 * SyncMonitoringPage — Synchronisation IBM i (v3)
 * Statut agent, boutons sync, résultats, historique
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  SyncIcon,
  RefreshIcon,
  CheckIcon,
  CloseIcon,
  DatabaseIcon,
  ActivityIcon,
  ShieldIcon
} from '../icons';
import { useViewport } from '../hooks/useViewport';
import ResponsiveCardList from '../components/responsive/ResponsiveCardList';
import { useHasPermission } from '../SupabaseWrapper';
import ClesApiAgents from '../components/sync/ClesApiAgents';
import { etatPolling } from '../shared/etatPolling';

// --- Types ---

interface SyncResult {
  total?: number;
  created?: number;
  updated?: number;
  failed?: number;
  error?: string;
  success?: boolean;
}

interface SyncLogEntry {
  id: string;
  direction: string;
  operation: string;
  entity_type: string;
  status: string;
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  dossier_code: string | null;
}

type SyncTarget = 'all' | 'statuses' | 'articles' | 'profiles' | 'interventions';

// L1.8/L1.9 (alignement CDC sur Pimer, 2026-09-14) : santé des agents (D-M, Q9) et audit CDC (Q8)
interface AgentReleve {
  establishment_id: string;
  code: string;
  dossier: string;
  agent_mode: string;
  cdc_mode: string;
  cdc_fenetre_nuit: string | null;
  cdc_fuseau: string | null;
  agent_status: {
    agent_id: string | null;
    agent_version: string | null;
    last_poll_at: string | null;
    last_cdc_post_at: string | null;
    backlog_evenements: number | null;
    backlog_age_secondes: number | null;
    updated_at: string;
  } | null;
  stats: { failed_24h: number; received_orphelins: number };
  sante: { niveau: 'ok' | 'alerte' | 'rollback' | 'inconnu'; motifs: string[] };
}

interface CdcEvent {
  id: number;
  establishment_id: string;
  dossier: string;
  event_id_remote: number;
  occurred_at: string;
  source_table: string;
  source_op: string;
  source_key: Record<string, unknown>;
  payload_present: boolean;
  status: 'received' | 'processed' | 'failed' | 'ignored';
  message: string | null;
  request_id: string | null;
  agent_id: string | null;
  received_at: string;
  processed_at: string | null;
}

type CdcStatusFilter = 'tous' | 'received' | 'processed' | 'failed' | 'ignored';

interface SyncMonitoringPageProps {
  refreshTrigger?: number;
}

// --- Config ---

const API_BASE = window.location.origin;

const SYNC_TARGETS: { key: SyncTarget; label: string; endpoint: string }[] = [
  { key: 'statuses', label: 'Statuts', endpoint: '/api/sync/ibmi/pull/statuses' },
  { key: 'articles', label: 'Articles', endpoint: '/api/sync/ibmi/pull/articles' },
  { key: 'profiles', label: 'Profils', endpoint: '/api/sync/ibmi/pull/profiles' },
  { key: 'interventions', label: 'Interventions', endpoint: '/api/sync/ibmi/pull/interventions' },
];

// --- Component ---

function SyncMonitoringPage({ refreshTrigger = 0 }: SyncMonitoringPageProps) {
  const { isPhone } = useViewport();
  // RBAC : permissions
  const canViewLogs = useHasPermission('sync.view_logs');
  const canRunSync = useHasPermission('sync.run');

  const [syncLogs, setSyncLogs] = useState<SyncLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<SyncTarget | null>(null);
  const [lastResult, setLastResult] = useState<Record<string, SyncResult> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [standalone, setStandalone] = useState(false);
  const [agents, setAgents] = useState<AgentReleve[]>([]);
  // Heure du relevé (evalue_le) : l'âge d'un poll se mesure à cette heure-là, pas à l'heure du rendu.
  const [agentsEvalueLe, setAgentsEvalueLe] = useState<Date>(() => new Date());
  const [cdcEvents, setCdcEvents] = useState<CdcEvent[]>([]);
  const [cdcFilter, setCdcFilter] = useState<CdcStatusFilter>('tous');
  const [cdcDetail, setCdcDetail] = useState<CdcEvent | null>(null);
  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({ show: false, type: 'success', message: '' });

  const getToken = () => localStorage.getItem('sso_token');

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: 'success', message: '' }), 4000);
  };

  // --- Fetch logs ---
  const fetchLogs = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      console.log('[SyncMonitoring] Chargement logs...');
      const response = await fetch(`${API_BASE}/api/sync/ibmi/logs?limit=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        if (body.error?.code === 'NOT_FULLY_MANAGED') {
          setStandalone(true);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const result = await response.json();
      if (result.success) setSyncLogs(result.logs || []);
    } catch (err: any) {
      console.error('[SyncMonitoring] Erreur logs:', err.message);
    }
  }, []);

  // --- Fetch agents (santé D-M par box) ---
  const fetchAgents = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/sync/ibmi/agents`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.success) {
        setAgents(result.agents || []);
        setAgentsEvalueLe(result.evalue_le ? new Date(result.evalue_le) : new Date());
      }
    } catch (err: any) {
      console.error('[SyncMonitoring] Erreur agents:', err.message);
    }
  }, []);

  // --- Fetch événements CDC (audit par événement) ---
  const fetchCdcEvents = useCallback(async (filtre: CdcStatusFilter) => {
    const token = getToken();
    if (!token) return;
    try {
      const q = filtre === 'tous' ? '' : `&status=${filtre}`;
      const response = await fetch(`${API_BASE}/api/sync/ibmi/cdc-events?limit=50${q}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.success) setCdcEvents(result.events || []);
    } catch (err: any) {
      console.error('[SyncMonitoring] Erreur événements CDC:', err.message);
    }
  }, []);

  // --- Fetch all ---
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchLogs(), fetchAgents(), fetchCdcEvents(cdcFilter)]);
    setLoading(false);
  }, [fetchLogs, fetchAgents, fetchCdcEvents, cdcFilter]);

  // Mount
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Le relevé des agents vit : un agent en long polling est vu toutes les 25 s au plus.
  useEffect(() => {
    const minuteur = setInterval(() => { fetchAgents(); }, 30000);
    return () => clearInterval(minuteur);
  }, [fetchAgents]);

  // refreshTrigger
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    console.log('[SyncMonitoring] refreshTrigger changed, refetching...');
    fetchAll();
  }, [refreshTrigger, fetchAll]);

  // --- Trigger sync (asynchrone) ---
  // Le backend répond 202 immédiatement et traite en arrière-plan (un sync IBM i
  // peut durer plusieurs minutes). On suit l'achèvement en pollant les sync_logs :
  // le backend y écrit l'entrée finale (success/error) une fois le job terminé.
  const triggerSync = async (target: SyncTarget) => {
    const token = getToken();
    if (!token) return;

    setSyncing(target);
    setError(null);
    setLastResult(null);

    // Lecture brute des logs, robuste (ne casse jamais sur réponse non-JSON)
    const fetchLogsRaw = async (): Promise<SyncLogEntry[]> => {
      try {
        const r = await fetch(`${API_BASE}/api/sync/ibmi/logs?limit=30`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!r.ok) return [];
        const j = await r.json().catch(() => ({}));
        return j.success ? (j.logs || []) : [];
      } catch {
        return [];
      }
    };

    // Quelles opérations de sync_logs correspondent à la cible demandée
    const matchOp = (op: string): boolean =>
      target === 'all' ? op.startsWith('pull_all') : op === target;

    try {
      const endpoint = target === 'all'
        ? '/api/sync/ibmi/pull'
        : SYNC_TARGETS.find(t => t.key === target)!.endpoint;

      console.log(`[SyncMonitoring] Sync ${target}...`);

      // Snapshot des logs existants pour repérer la nouvelle entrée
      const beforeIds = new Set((await fetchLogsRaw()).map(l => l.id));

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      // Réponse non-JSON (ex : page HTML 504 d'une passerelle) → ne pas planter
      // sur .json() ; le job a tout de même pu démarrer côté serveur.
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        showToast('success', `${target} : synchronisation lancée — suivi en cours…`);
      } else {
        const result = await response.json();
        console.log('[SyncMonitoring] Réponse:', result);
        if (!response.ok && !result.started) {
          const msg = result.error?.message || `Erreur HTTP ${response.status}`;
          setError(msg);
          showToast('error', msg);
          await fetchLogs();
          return;
        }
        showToast('success', `${target} : synchronisation lancée…`);
      }

      // Suivi de l'achèvement : poll des sync_logs jusqu'à la nouvelle entrée terminée
      const deadline = Date.now() + 15 * 60 * 1000; // 15 min max
      let done: SyncLogEntry[] | null = null;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 4000));
        const logs = await fetchLogsRaw();
        if (logs.length) setSyncLogs(logs);
        const fresh = logs.filter(l =>
          !beforeIds.has(l.id) && matchOp(l.operation) &&
          (l.status === 'success' || l.status === 'error')
        );
        if (fresh.length > 0) { done = fresh; break; }
      }

      await fetchLogs();

      if (done) {
        const anyError = done.some(l => l.status === 'error');
        if (target === 'all') {
          const created = done.reduce((s, l) => s + (l.records_created || 0), 0);
          const updated = done.reduce((s, l) => s + (l.records_updated || 0), 0);
          showToast(anyError ? 'error' : 'success',
            anyError ? 'Sync terminée avec des erreurs (voir historique)' : `Sync terminée : ${created} créé(s), ${updated} modifié(s)`);
        } else {
          const l = done[0];
          setLastResult({ [target]: {
            total: l.records_processed, created: l.records_created,
            updated: l.records_updated, failed: l.records_failed,
            error: l.error_message || undefined
          } });
          if (l.status === 'error') showToast('error', `${target} : ${l.error_message || 'erreur de synchronisation'}`);
          else showToast('success', `${target} : ${l.records_processed} traité(s), ${l.records_created} créé(s), ${l.records_updated} modifié(s)`);
        }
      } else {
        showToast('success', `${target} : sync toujours en cours en arrière-plan — consulte l'historique.`);
      }
    } catch (err: any) {
      console.error('[SyncMonitoring] Erreur sync:', err);
      setError(err.message);
      showToast('error', `Erreur : ${err.message}`);
    } finally {
      setSyncing(null);
    }
  };

  // --- Helpers ---
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const formatDuration = (ms: number | null) => {
    if (ms === null || ms === undefined) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const directionBadge = (dir: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      pull: { bg: '#dbeafe', text: '#1d4ed8' },
      cdc: { bg: '#ede9fe', text: '#6d28d9' },
      writeback: { bg: '#ffedd5', text: '#c2410c' },
    };
    const c = colors[dir] || { bg: '#f1f5f9', text: '#475569' };
    return (
      <span style={{ padding: '2px 8px', backgroundColor: c.bg, color: c.text, borderRadius: '4px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' as const }}>
        {dir}
      </span>
    );
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      success: { bg: '#dcfce7', text: '#166534' },
      partial: { bg: '#fef3c7', text: '#92400e' },
      error: { bg: '#fef2f2', text: '#dc2626' },
    };
    const c = colors[status] || { bg: '#f1f5f9', text: '#475569' };
    return (
      <span style={{ padding: '2px 8px', backgroundColor: c.bg, color: c.text, borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
        {status}
      </span>
    );
  };

  const santeBadge = (niveau: AgentReleve['sante']['niveau']) => {
    const colors: Record<string, { bg: string; text: string; label: string }> = {
      ok: { bg: '#dcfce7', text: '#166534', label: 'OK' },
      alerte: { bg: '#fef3c7', text: '#92400e', label: 'Alerte' },
      rollback: { bg: '#fef2f2', text: '#dc2626', label: 'Rollback' },
      inconnu: { bg: '#f1f5f9', text: '#475569', label: 'Inconnu' },
    };
    const c = colors[niveau] || colors.inconnu;
    return (
      <span style={{ padding: '2px 8px', backgroundColor: c.bg, color: c.text, borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
        {c.label}
      </span>
    );
  };

  const cdcStatusBadge = (status: CdcEvent['status']) => {
    const colors: Record<string, { bg: string; text: string; label: string }> = {
      received: { bg: '#dbeafe', text: '#1e40af', label: 'Reçu' },
      processed: { bg: '#dcfce7', text: '#166534', label: 'Traité' },
      failed: { bg: '#fef2f2', text: '#dc2626', label: 'Échec' },
      ignored: { bg: '#f1f5f9', text: '#475569', label: 'Ignoré' },
    };
    const c = colors[status] || colors.received;
    return (
      <span style={{ padding: '2px 8px', backgroundColor: c.bg, color: c.text, borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
        {c.label}
      </span>
    );
  };

  const formatAge = (iso: string | null) => {
    if (!iso) return '—';
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `il y a ${s} s`;
    if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
    if (s < 86400) return `il y a ${Math.round(s / 3600)} h`;
    return `il y a ${Math.round(s / 86400)} j`;
  };

  const formatSecondes = (n: number | null) => {
    if (n === null || n === undefined) return '—';
    if (n < 60) return `${n} s`;
    if (n < 3600) return `${Math.round(n / 60)} min`;
    return `${(n / 3600).toFixed(1)} h`;
  };

  const CDC_FILTERS: { key: CdcStatusFilter; label: string }[] = [
    { key: 'tous', label: 'Tous' }, { key: 'received', label: 'Reçus' }, { key: 'processed', label: 'Traités' },
    { key: 'failed', label: 'Échecs' }, { key: 'ignored', label: 'Ignorés' },
  ];

  // Bandeau et gate de synchro : l'état du POLLING par établissement (demande du 17/09).
  // Le relais HTTP est banni ; ce qui compte est que l'agent de chaque établissement vienne
  // chercher son travail (long polling). Logique pure et testée : shared/etatPolling.ts.
  const polling = etatPolling(agents, agentsEvalueLe);
  const agentConnected = polling.lectureDb2Possible;

  // Gate d'entree : permission sync.view_logs
  if (!canViewLogs) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
        <ShieldIcon size={64} color="#ef4444" />
        <h2 style={{ color: '#1e293b', margin: 0 }}>Accès refusé</h2>
        <p style={{ color: '#64748b', margin: 0 }}>Vous n'avez pas les autorisations nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  // --- Loading ---
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <RefreshIcon size={32} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '12px', color: '#64748b' }}>Chargement...</span>
      </div>
    );
  }

  // --- Standalone mode ---
  if (standalone) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <DatabaseIcon size={48} color="#94a3b8" />
        <h3 style={{ color: '#475569', marginTop: '16px' }}>Mode autonome</h3>
        <p style={{ color: '#94a3b8', maxWidth: '400px', margin: '8px auto' }}>
          Cette organisation fonctionne en mode standalone. La synchronisation IBM i n'est pas disponible.
        </p>
      </div>
    );
  }

  // --- Render ---
  return (
    <div style={{ padding: '0', position: 'relative' }}>
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 1000,
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '12px 20px',
          backgroundColor: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${toast.type === 'success' ? '#86efac' : '#fecaca'}`,
          borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.3s ease'
        }}>
          <span style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '24px', height: '24px', borderRadius: '50%',
            backgroundColor: toast.type === 'success' ? '#22c55e' : '#ef4444',
            color: 'white', fontSize: '14px', fontWeight: 'bold'
          }}>
            {toast.type === 'success' ? '✓' : '✕'}
          </span>
          <span style={{ color: toast.type === 'success' ? '#166534' : '#dc2626', fontWeight: 500, fontSize: '14px' }}>
            {toast.message}
          </span>
          <button onClick={() => setToast({ ...toast, show: false })} style={{
            marginLeft: '8px', padding: '4px', backgroundColor: 'transparent',
            border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px', lineHeight: 1
          }}>×</button>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <button
          onClick={() => fetchAll()}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 16px', backgroundColor: 'white', color: '#475569',
            border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer',
            fontSize: '14px', fontWeight: 500
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
        >
          <RefreshIcon size={18} />
          Actualiser
        </button>
      </div>

      {/* Section 1 : état du polling des agents IBM i */}
      {(() => {
        const teinte = polling.niveau === 'ok'
          ? { fond: '#f0fdf4', bord: '#bbf7d0', point: '#22c55e', texte: '#166534' }
          : polling.niveau === 'attention'
            ? { fond: '#fffbeb', bord: '#fde68a', point: '#f59e0b', texte: '#92400e' }
            : { fond: '#f8fafc', bord: '#e2e8f0', point: '#94a3b8', texte: '#475569' };
        return (
          <div style={{
            padding: '16px 20px', borderRadius: '12px', marginBottom: '20px',
            backgroundColor: teinte.fond, border: `1px solid ${teinte.bord}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: teinte.point, display: 'inline-block' }} />
              <span style={{ fontWeight: 600, color: teinte.texte }}>{polling.titre}</span>
              {polling.detail && (
                <span style={{ fontSize: '13px', color: '#64748b', marginLeft: 'auto' }}>{polling.detail}</span>
              )}
            </div>
            {polling.lignes.length > 0 && (
              <ul style={{ margin: '8px 0 0 22px', padding: 0, listStyle: 'none', fontSize: '13px', color: '#475569' }}>
                {polling.lignes.map((l) => (
                  <li key={l.code} style={{ color: l.ok ? '#475569' : '#92400e' }}>{l.texte}</li>
                ))}
              </ul>
            )}
            {polling.niveau === 'inconnu' && (
              <p style={{ fontSize: '13px', color: '#64748b', margin: '8px 0 0 22px' }}>
                Aucun établissement relié à un IBM i dans cette organisation.
              </p>
            )}
          </div>
        );
      })()}

      {/* Section 2 : Boutons sync (visibles seulement si canRunSync) */}
      {canRunSync && (
        <div style={{
          padding: '20px', borderRadius: '12px', marginBottom: '20px',
          backgroundColor: 'white', border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {/* Bouton principal */}
            <button
              onClick={() => triggerSync('all')}
              disabled={syncing !== null || !agentConnected}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 24px', backgroundColor: (!agentConnected || syncing !== null) ? '#94a3b8' : '#3b82f6',
                color: 'white', border: 'none', borderRadius: '8px',
                cursor: (!agentConnected || syncing !== null) ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: 600
              }}
            >
              <SyncIcon size={18} style={{ animation: syncing === 'all' ? 'spin 1s linear infinite' : 'none' }} />
              {syncing === 'all' ? 'Synchronisation...' : 'Synchroniser tout'}
            </button>

            <span style={{ width: '1px', height: '32px', backgroundColor: '#e2e8f0' }} />

            {/* Boutons individuels */}
            {SYNC_TARGETS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => triggerSync(key)}
                disabled={syncing !== null || !agentConnected}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: syncing === key ? '#eff6ff' : 'white',
                  color: (!agentConnected || syncing !== null) ? '#94a3b8' : '#3b82f6',
                  border: `1px solid ${syncing === key ? '#3b82f6' : '#cbd5e1'}`,
                  borderRadius: '8px',
                  cursor: (!agentConnected || syncing !== null) ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: 500
                }}
                onMouseEnter={(e) => { if (agentConnected && !syncing) e.currentTarget.style.borderColor = '#3b82f6'; }}
                onMouseLeave={(e) => { if (syncing !== key) e.currentTarget.style.borderColor = '#cbd5e1'; }}
              >
                {syncing === key && <SyncIcon size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div style={{
          padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '8px', color: '#dc2626', marginBottom: '20px',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <CloseIcon size={18} />
          {error}
        </div>
      )}

      {/* Section 3 : Résultat dernière sync */}
      {lastResult && (
        <div style={{
          padding: '16px 20px', borderRadius: '12px', marginBottom: '20px',
          backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckIcon size={20} color="#22c55e" />
              <span style={{ fontWeight: 600, color: '#166534' }}>Résultat de la synchronisation</span>
            </div>
            <button onClick={() => setLastResult(null)} style={{
              padding: '4px', backgroundColor: 'transparent', border: 'none',
              cursor: 'pointer', color: '#94a3b8'
            }}>
              <CloseIcon size={16} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : `repeat(${Object.keys(lastResult).length}, 1fr)`, gap: isPhone ? 12 : 16 }}>
            {Object.entries(lastResult).map(([key, r]) => (
              <div key={key} style={{
                padding: '12px', backgroundColor: 'white', borderRadius: '8px',
                border: r.error ? '1px solid #fecaca' : '1px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase' as const, fontWeight: 600, marginBottom: '8px' }}>
                  {key}
                </div>
                {r.error ? (
                  <div style={{ color: '#dc2626', fontSize: '13px' }}>{r.error}</div>
                ) : (
                  <div style={{ display: 'flex', gap: '12px', fontSize: '13px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#475569' }}><strong>{r.total || 0}</strong> traité(s)</span>
                    <span style={{ color: '#22c55e' }}>+{r.created || 0}</span>
                    <span style={{ color: '#3b82f6' }}>{r.updated || 0} maj</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 3 bis : Agents IBM i — santé D-M par box (L1.9) */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '20px' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ShieldIcon size={20} color="#0ea5e9" />
          <span style={{ fontWeight: 600, color: '#1e293b' }}>Agents IBM i</span>
          <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: 'auto', padding: '2px 8px', backgroundColor: '#f1f5f9', borderRadius: '10px' }}>
            {agents.length} établissement(s)
          </span>
        </div>
        {agents.length === 0 ? (
          <div style={{ padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Aucun établissement relevé</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px', padding: '16px 20px' }}>
            {agents.map(a => (
              <div key={a.establishment_id} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <strong style={{ color: '#1e293b' }}>{a.dossier}</strong>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>{a.code}</span>
                  <span style={{ marginLeft: 'auto' }}>{santeBadge(a.sante.niveau)}</span>
                </div>
                <div style={{ fontSize: '12px', color: '#475569', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <span>CDC : <strong>{a.cdc_mode === 'agent' ? 'agent' : 'listener'}</strong></span>
                  <span>descendant : <strong>{a.agent_mode}</strong></span>
                  {a.cdc_fenetre_nuit && <span>nuit : {a.cdc_fenetre_nuit}{a.cdc_fuseau ? ` (${a.cdc_fuseau})` : ''}</span>}
                </div>
                {a.agent_status ? (
                  <div style={{ fontSize: '12px', color: '#475569', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <span>backlog : <strong>{a.agent_status.backlog_evenements ?? '—'}</strong></span>
                    <span>retard : <strong>{formatSecondes(a.agent_status.backlog_age_secondes)}</strong></span>
                    <span>dernier poll : {formatAge(a.agent_status.last_poll_at)}</span>
                    <span>dernier CDC : {formatAge(a.agent_status.last_cdc_post_at)}</span>
                    {a.agent_status.agent_version && <span>v{a.agent_status.agent_version}</span>}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>Aucun état d'agent remonté</div>
                )}
                {(a.stats.failed_24h > 0 || a.stats.received_orphelins > 0) && (
                  <div style={{ fontSize: '12px', color: '#92400e' }}>
                    {a.stats.failed_24h > 0 && <span>{a.stats.failed_24h} échec(s) sur 24 h · </span>}
                    {a.stats.received_orphelins > 0 && <span>{a.stats.received_orphelins} reçu(s) sans suite</span>}
                  </div>
                )}
                {a.sante.motifs.length > 0 && a.sante.niveau !== 'ok' && (
                  <ul style={{ margin: '4px 0 0', paddingLeft: '18px', fontSize: '12px', color: a.sante.niveau === 'rollback' ? '#dc2626' : '#92400e' }}>
                    {a.sante.motifs.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 3 bis-2 : Clés API des agents IBM i (écran du 2026-09-16) */}
      <ClesApiAgents />

      {/* Section 3 ter : Événements CDC — audit par événement (L1.9) */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '20px' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <DatabaseIcon size={20} color="#0ea5e9" />
          <span style={{ fontWeight: 600, color: '#1e293b' }}>Événements CDC</span>
          <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto', flexWrap: 'wrap' }}>
            {CDC_FILTERS.map(f => (
              <button key={f.key} onClick={() => { setCdcFilter(f.key); setCdcDetail(null); fetchCdcEvents(f.key); }}
                style={{
                  padding: '4px 10px', borderRadius: '12px', fontSize: '12px', cursor: 'pointer',
                  border: '1px solid ' + (cdcFilter === f.key ? '#0ea5e9' : '#e2e8f0'),
                  backgroundColor: cdcFilter === f.key ? '#e0f2fe' : 'white', color: cdcFilter === f.key ? '#0369a1' : '#475569', fontWeight: cdcFilter === f.key ? 600 : 400,
                }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        {cdcEvents.length === 0 ? (
          <div style={{ padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Aucun événement CDC reçu par l'agent</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  {['Reçu le', 'Dossier', 'EVT_ID', 'Table', 'Op', 'Clé', 'Statut', 'Message'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '12px', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cdcEvents.map(e => (
                  <tr key={e.id} onClick={() => setCdcDetail(cdcDetail?.id === e.id ? null : e)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', backgroundColor: cdcDetail?.id === e.id ? '#f0f9ff' : 'transparent' }}>
                    <td style={{ padding: '8px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDate(e.received_at)}</td>
                    <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500 }}>{e.dossier}</td>
                    <td style={{ padding: '8px 12px', color: '#475569', fontFamily: 'monospace' }}>{e.event_id_remote}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{e.source_table}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{e.source_op}</td>
                    <td style={{ padding: '8px 12px', color: '#64748b', fontFamily: 'monospace', fontSize: '12px' }}>{JSON.stringify(e.source_key)}</td>
                    <td style={{ padding: '8px 12px' }}>{cdcStatusBadge(e.status)}</td>
                    <td style={{ padding: '8px 12px', color: e.status === 'failed' ? '#dc2626' : '#64748b', fontSize: '12px', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.message || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cdcDetail && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc', fontSize: '12px', color: '#475569' }}>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '6px' }}>
              <span>EVT_ID <strong>{cdcDetail.event_id_remote}</strong></span>
              <span>occurred_at (box) <code>{cdcDetail.occurred_at}</code></span>
              <span>reçu {formatDate(cdcDetail.received_at)}</span>
              {cdcDetail.processed_at && <span>traité {formatDate(cdcDetail.processed_at)}</span>}
              {cdcDetail.agent_id && <span>agent <code>{cdcDetail.agent_id}</code></span>}
              {cdcDetail.request_id && <span>requête <code>{cdcDetail.request_id}</code></span>}
              <span>payload {cdcDetail.payload_present ? 'présent' : 'absent'}</span>
            </div>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>{JSON.stringify(cdcDetail.source_key, null, 2)}</pre>
            {cdcDetail.message && <div style={{ marginTop: '6px', color: cdcDetail.status === 'failed' ? '#dc2626' : '#475569' }}>{cdcDetail.message}</div>}
          </div>
        )}
      </div>

      {/* Section 4 : Historique sync_logs */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: '10px'
        }}>
          <ActivityIcon size={20} color="#8b5cf6" />
          <span style={{ fontWeight: 600, color: '#1e293b' }}>Historique des synchronisations</span>
          <span style={{
            fontSize: '12px', color: '#94a3b8', marginLeft: 'auto',
            padding: '2px 8px', backgroundColor: '#f1f5f9', borderRadius: '10px'
          }}>
            {syncLogs.length} entrée(s)
          </span>
        </div>

        {syncLogs.length === 0 && !isPhone ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8' }}>
            <DatabaseIcon size={32} color="#cbd5e1" />
            <div style={{ marginTop: '12px' }}>Aucun log de synchronisation</div>
            <div style={{ fontSize: '13px', marginTop: '4px' }}>Les logs apparaîtront après la première synchronisation.</div>
          </div>
        ) : (
          <ResponsiveCardList
            items={syncLogs}
            renderCard={(log) => (
              <div style={{
                backgroundColor: 'white',
                borderRadius: 12,
                padding: 14,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>
                    {formatDate(log.started_at)}
                  </div>
                  {statusBadge(log.status)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {directionBadge(log.direction)}
                  <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>{log.operation}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13 }}>
                  <span style={{ color: '#1e293b' }}><strong>{log.records_processed}</strong> traités</span>
                  {log.records_created > 0 && (
                    <span style={{ color: '#22c55e' }}>+{log.records_created} créés</span>
                  )}
                  {log.records_updated > 0 && (
                    <span style={{ color: '#3b82f6' }}>{log.records_updated} maj</span>
                  )}
                  {log.records_failed > 0 && (
                    <span style={{ color: '#dc2626' }}>{log.records_failed} erreurs</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  Durée : {formatDuration(log.duration_ms)}
                </div>
                {log.error_message && (
                  <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4, padding: 8, backgroundColor: '#fef2f2', borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                    {log.error_message}
                  </div>
                )}
              </div>
            )}
            fallback={
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      {['Date', 'Direction', 'Opération', 'Statut', 'Traités', 'Créés', 'Modifiés', 'Erreurs', 'Durée'].map(h => (
                        <th key={h} style={{
                          padding: '10px 12px', textAlign: 'left', color: '#64748b',
                          fontWeight: 600, fontSize: '12px', borderBottom: '1px solid #e2e8f0'
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {syncLogs.map(log => (
                      <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 12px', color: '#475569', whiteSpace: 'nowrap' }}>
                          {formatDate(log.started_at)}
                        </td>
                        <td style={{ padding: '10px 12px' }}>{directionBadge(log.direction)}</td>
                        <td style={{ padding: '10px 12px', color: '#475569' }}>{log.operation}</td>
                        <td style={{ padding: '10px 12px' }}>{statusBadge(log.status)}</td>
                        <td style={{ padding: '10px 12px', color: '#1e293b', fontWeight: 500 }}>{log.records_processed}</td>
                        <td style={{ padding: '10px 12px', color: '#22c55e' }}>{log.records_created > 0 ? `+${log.records_created}` : '0'}</td>
                        <td style={{ padding: '10px 12px', color: '#3b82f6' }}>{log.records_updated}</td>
                        <td style={{ padding: '10px 12px', color: log.records_failed > 0 ? '#dc2626' : '#94a3b8' }}>
                          {log.records_failed}
                        </td>
                        <td style={{ padding: '10px 12px', color: '#64748b' }}>{formatDuration(log.duration_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
            emptyMessage="Aucun log de synchronisation"
          />
        )}
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default SyncMonitoringPage;
